import { createClient } from "@supabase/supabase-js";

const url = process.env.REACT_APP_SUPABASE_URL;
const key = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const STORAGE_KEY = "clientdesk-auth";

/**
 * Supabase JS v2 uses `navigator.locks` internally to serialize session reads.
 * Under React.StrictMode (dev double-mount) this causes hangs where two
 * concurrent `getSession()` calls deadlock on the same lock. Passing a
 * pass-through `lock` function disables this behaviour — safe in browser JS
 * since we're single-threaded.
 * Reference: https://github.com/supabase/auth-js  `lock` option.
 */
const passthroughLock = async (_name, _acquireTimeout, fn) => await fn();

export const supabase = createClient(url, key, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: STORAGE_KEY,
    storage: typeof window !== "undefined" ? window.localStorage : undefined,
    flowType: "pkce",
    lock: passthroughLock,
  },
});

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Read the current access token directly from localStorage. This bypasses
 * supabase.auth.getSession() entirely — which is the source of the strict-mode
 * lock deadlock — and is safe because supabase-js persists the session there
 * under our storageKey. Returns null if there is no session or it's malformed.
 */
function readAccessToken() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.access_token || null;
  } catch (_) {
    return null;
  }
}

/**
 * Authenticated fetch to our FastAPI backend. On 401 we transparently attempt
 * a session refresh + retry once — this fixes the flash-of-401s that happens
 * when a request lands during the ~1-2 second Supabase auto-refresh window.
 */
export async function apiFetch(path, options = {}) {
  const doFetch = async () => {
    const token = readAccessToken();
    const headers = {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(`${API}${path}`, { ...options, headers });
  };

  let res = await doFetch();

  if (res.status === 401) {
    // P0 fix: do NOT blindly call refreshSession on every 401. Backend
    // 401s that are unrelated to the primary session (expired Google /
    // Outlook calendar OAuth, an RLS denial that surfaces as 401) used
    // to trigger a refresh whose failure purged the Supabase session
    // and forced a logout mid-booking. Only attempt a refresh when the
    // local access token is actually expired/near-expiry AND swallow
    // any refresh failure so it never destroys the primary session.
    try {
      const { data } = await supabase.auth.getSession();
      const exp = data?.session?.expires_at;
      const nowSec = Math.floor(Date.now() / 1000);
      const nearExpiry = !exp || exp - nowSec < 60;
      if (nearExpiry) {
        try { await supabase.auth.refreshSession(); } catch (_) { /* keep session */ }
      }
    } catch (_) { /* ignore */ }
    res = await doFetch();
  }

  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      const d = j.detail ?? j.message ?? j;
      if (Array.isArray(d)) {
        detail = d.map((e) => e.msg || e.message || JSON.stringify(e)).join("; ");
      } else if (typeof d === "string") {
        detail = d;
      } else if (d && typeof d === "object") {
        detail = d.msg || d.message || JSON.stringify(d);
      }
    } catch (_) {}
    const err = new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
    err.status = res.status;
    throw err;
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res.text();
}
